import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Camera, ImagePlus, Trash2, ScanSearch, Lightbulb, ArrowRight, Home, Sofa } from 'lucide-react';
import { useT } from '../studioI18n';
import { useProject } from '../ProjectContext';
import { studioApi, apiError, fileUrl, compressImage } from '../studioApi';
import { Page, MotionButton, stagger, rise, pop } from '../components/Motion';
import { SmartImg } from '../components/SmartImg';
import { DetectProgress } from '../../components/DetectProgress';

const MAX = 6;

const PhotoCard = ({ photo, index, preview, etaS, onDelete, onKind }) => {
  const { t } = useT();
  const detecting = photo.windows_status === 'detecting';
  const n = (photo.windows || []).length;
  return (
    <motion.div variants={pop} layout exit={{ opacity: 0, scale: 0.85 }} className="relative rounded-[1.5rem] overflow-hidden st-glass group" data-testid={`photo-thumbnail-${index}`}>
      <SmartImg src={fileUrl(photo.id, 'thumb')} placeholder={preview} alt="" className="w-full aspect-[4/3] object-cover" />
      <div className="absolute inset-x-0 top-0 p-2 flex items-start justify-between gap-2">
        {!detecting && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold backdrop-blur ${n ? 'bg-teal-600 text-white' : 'bg-slate-800/70 text-white'}`} data-testid={`ai-detection-badge-${index}`}>
            <ScanSearch className="h-3.5 w-3.5" /> {n ? t.windowsFound(n) : t.noWindows}
          </span>
        )}
        <motion.button whileTap={{ scale: 0.9 }} type="button" onClick={() => onDelete(photo)} className="h-8 w-8 rounded-full bg-white/90 text-rose-600 flex items-center justify-center shadow ml-auto" aria-label={t.deletePhoto} data-testid={`delete-photo-btn-${index}`}><Trash2 className="h-4 w-4" /></motion.button>
      </div>
      {detecting && (
        <div className="absolute inset-x-0 top-12 px-2" data-testid={`ai-detection-badge-${index}`}>
          <div className="rounded-2xl bg-white/90 backdrop-blur px-3 py-2 text-teal-900 shadow">
            <DetectProgress startedAt={photo.windows_started_at || photo.created_at} etaS={etaS} label={t.detecting} remainingLabel={t.detectRemaining} almostLabel={t.detectAlmost} testId={`detect-progress-${index}`} />
          </div>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-2 flex justify-center">
        <div className="inline-flex rounded-full bg-white/90 backdrop-blur p-0.5 text-[11px] font-bold shadow" data-testid={`photo-kind-toggle-${index}`}>
          {['exterior', 'interior'].map((k) => (
            <button key={k} type="button" onClick={() => onKind(photo, k)} className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-full transition-colors ${photo.photo_kind === k ? 'bg-slate-900 text-white' : 'text-slate-600'}`} data-testid={`photo-kind-${k}-${index}`}>{k === 'exterior' ? <Home className="h-3 w-3" /> : <Sofa className="h-3 w-3" />} {k === 'exterior' ? t.exterior : t.interior}</button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export const UploadStep = () => {
  const { t } = useT();
  const { project, reload, addPhoto } = useProject();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]); // {id, pct, preview}
  const [previews, setPreviews] = useState({}); // photo.id -> blob URL local (aparece na hora, antes do thumb do servidor)
  const previewsRef = useRef({});
  previewsRef.current = previews;
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const photos = project.photos;
  const photoIds = photos.map((p) => p.id).join(',');

  useEffect(() => () => { Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u)); }, []);
  useEffect(() => { photoIds.split(',').filter(Boolean).forEach((id) => { const im = new Image(); im.src = fileUrl(id); }); }, [photoIds]);

  const handleFiles = async (list) => {
    const files = Array.from(list || []).filter((f) => f.type.startsWith('image/'));
    const room = MAX - photos.length - uploads.length;
    if (room <= 0) { toast.error(t.maxReached); return; }
    for (const file of files.slice(0, room)) {
      const id = `${Date.now()}-${Math.random()}`;
      const preview = URL.createObjectURL(file);
      setUploads((u) => [...u, { id, pct: 0, preview }]);
      try {
        const blob = await compressImage(file);
        const fd = new FormData();
        fd.append('file', blob, file.name || 'photo.jpg');
        fd.append('kind', 'exterior');
        const { data } = await studioApi.post('/photos', fd, { onUploadProgress: (e) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, pct: Math.round((e.loaded / (e.total || 1)) * 100) } : x))) });
        if (data?.id) { setPreviews((m) => ({ ...m, [data.id]: preview })); addPhoto(data); }
        toast.success(t.photoUploaded);
      } catch (e) { toast.error(apiError(e)); } finally { setUploads((u) => u.filter((x) => x.id !== id)); }
    }
    reload().catch(() => {});
  };
  const onDelete = async (p) => { try { await studioApi.delete(`/photos/${p.id}`); toast.success(t.photoDeleted); await reload(); } catch (e) { toast.error(apiError(e)); } };
  const onKind = async (p, kind) => { if (p.photo_kind === kind) return; try { await studioApi.put(`/photos/${p.id}`, { photo_kind: kind }); await reload(); } catch (e) { toast.error(apiError(e)); } };
  const full = photos.length + uploads.length >= MAX;

  return (
    <Page testId="upload-step">
      <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
        <motion.div variants={rise} className="text-center max-w-xl mx-auto">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{t.upTitle}</h1>
          <p className="text-slate-600 mt-2">{t.upSub}</p>
          <p className="text-xs font-bold text-teal-700 mt-3 tabular-nums" data-testid="photos-counter">{t.photosOf(photos.length, MAX)}</p>
        </motion.div>

        <motion.div variants={rise}>
          <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
            onClick={() => !full && fileRef.current?.click()} role="button" tabIndex={0}
            className={`relative rounded-[2rem] border-2 border-dashed p-8 sm:p-12 text-center transition-all cursor-pointer ${drag ? 'border-teal-500 bg-teal-50/80 scale-[1.01]' : 'border-teal-600/30 bg-white/50 hover:bg-white/70'} ${full ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="upload-zone">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="upload-file-input" />
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="upload-camera-input" />
            <motion.span animate={{ y: drag ? -6 : 0 }} className="mx-auto h-16 w-16 rounded-3xl bg-teal-600 text-white flex items-center justify-center shadow-xl shadow-teal-600/30"><ImagePlus className="h-8 w-8" /></motion.span>
            <p className="font-display font-bold text-slate-900 mt-4">{full ? t.maxReached : t.dropHere}</p>
            {!full && (
              <div className="flex flex-wrap justify-center gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                <MotionButton type="button" variant="ghost" className="h-11 text-sm" onClick={() => fileRef.current?.click()} data-testid="pick-photos-btn"><ImagePlus className="h-4 w-4" /> {t.pickPhotos}</MotionButton>
                <span className="sm:hidden"><MotionButton type="button" variant="dark" className="h-11 text-sm" onClick={() => camRef.current?.click()} data-testid="take-photo-btn"><Camera className="h-4 w-4" /> {t.takePhoto}</MotionButton></span>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          <AnimatePresence>
            {photos.map((p, i) => <PhotoCard key={p.id} photo={p} index={i} preview={previews[p.id]} etaS={project.detect_eta_s} onDelete={onDelete} onKind={onKind} />)}
            {uploads.map((u) => (
              <motion.div key={u.id} variants={pop} initial="initial" animate="animate" exit={{ opacity: 0 }} className="relative rounded-[1.5rem] overflow-hidden st-glass" data-testid="photo-uploading">
                <img src={u.preview} alt="" className="w-full aspect-[4/3] object-cover opacity-60" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/40 backdrop-blur-[2px]">
                  <span className="text-xs font-bold text-teal-800">{t.uploading} {u.pct}%</span>
                  <div className="h-2 w-2/3 rounded-full bg-white/80 overflow-hidden"><motion.div className="h-full bg-teal-600 st-stripes" animate={{ width: `${Math.max(8, u.pct)}%` }} /></div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <motion.div variants={rise} className="st-glass rounded-[1.5rem] p-4 flex items-start gap-3 text-sm"><Lightbulb className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" /><p className="text-slate-600"><b className="text-slate-900">{t.upTipT}:</b> {t.upTip}</p></motion.div>
      </motion.div>

      <div className="fixed bottom-0 inset-x-0 z-30 p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-t border-white/80" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <div className="max-w-6xl mx-auto flex justify-end">
          <MotionButton onClick={() => navigate('/project/design')} disabled={!photos.length} className="w-full sm:w-auto" data-testid="proceed-to-configure-btn">{t.continue} <ArrowRight className="h-5 w-5" /></MotionButton>
        </div>
      </div>
    </Page>
  );
};
